// replay.js — deterministic replay of a persisted turn (self-improve-hardening E4-WS3).
//
// THE BINARY GATE of Stage 1: a run that FAILED (e.g. `autocorrect_cap`) re-runs
// from its persisted trace to the SAME outcome. Replay re-runs the DECISION the
// AUTO_CORRECT gate made — over the RECORDED oracle verdicts — NOT the verifiers
// or the model. There is no live network on this path: where the trace already
// HAS the answer (the verdicts), we re-derive from it. That is what makes it
// deterministic (no Date.now / Math.random / network in the decision path).
//
// The decision function is SHARED with the live loop (`classifyOracleStep`), so
// replay can never drift from production — it is the same code, not a mirror.
//
// PURE: no I/O, never throws. A garbage record degrades to a safe, explicit shape.

import { classifyOracleStep, AUTO_CORRECT_CAP } from './oracle.js';

// stopReasons the loop emits that mean the turn reached DONE (vs an ABORT).
const DONE_STOPS = new Set(['done', 'done_after_budget', 'plan_prose', 'conversational_reply']);

/**
 * Re-derive a persisted turn's terminal outcome.
 *
 * @param {object} record persisted TurnTrace ({ outcome, stopReason, attempts, verdicts, ... })
 * @returns {{ outcome: 'DONE'|'ABORT'|'INCOMPLETE', stopReason: string|null,
 *             reachedGreen: boolean, attempts: number, deterministic: boolean,
 *             matches: boolean }}
 *   - deterministic: true when the outcome was re-DERIVED from the recorded oracle
 *     verdicts (the auto-correct gate). false for transport/context/model failures
 *     that have no re-runnable decision input — those are honored as recorded
 *     (re-calling a live model would itself break determinism).
 *   - matches: re-derived outcome === the recorded outcome (the gate assertion).
 */
export function replayTurnTrace(record) {
  const rec = record && typeof record === 'object' ? record : {};
  const verdicts = Array.isArray(rec.verdicts) ? rec.verdicts : [];
  const attempts = Number.isFinite(rec.attempts) ? rec.attempts : 0;

  // Oracle-driven iff the auto-correct gate produced the terminal decision: either
  // the cap aborted it, or it finished with recorded verifier verdicts.
  const oracleDriven = rec.stopReason === 'autocorrect_cap' || verdicts.length > 0;

  if (!oracleDriven) {
    const outcome = DONE_STOPS.has(rec.stopReason)
      ? 'DONE'
      : (rec.outcome === 'DONE' ? 'DONE' : 'ABORT');
    return {
      outcome,
      stopReason: rec.stopReason ?? null,
      reachedGreen: false,
      attempts,
      deterministic: false,
      matches: rec.outcome ? outcome === rec.outcome : true,
    };
  }

  const reachedGreen = verdicts.length > 0 && verdicts.every((v) => v && v.passed === true);
  const noVerifiers = verdicts.length === 0;
  const step = classifyOracleStep({ reachedGreen, noVerifiers, attempts, cap: AUTO_CORRECT_CAP });
  // T01 (tier-s-sprint-unified-2026-06-28) — step may be string (legacy) or
  // { outcome, unverified } (tier-s). Normalize.
  const stepOutcome = typeof step === 'string' ? step : step.outcome;
  const outcome = stepOutcome === 'abort' ? 'ABORT' : (stepOutcome === 'green' ? 'DONE' : 'INCOMPLETE');
  const stopReason = stepOutcome === 'abort' ? 'autocorrect_cap' : (rec.stopReason ?? 'done');

  return {
    outcome,
    stopReason,
    reachedGreen,
    attempts,
    deterministic: true,
    matches: rec.outcome ? outcome === rec.outcome : true,
  };
}

/** Convenience boolean for the gate: did replay reproduce the recorded outcome? */
export function replayMatches(record) {
  return replayTurnTrace(record).matches === true;
}
